// Shared by browser code (buffer, middleware) and server code (request schema, prompt,
// transcript), so it imports nothing.

// One logged app action, in the same shape as the request posts and the server stores.
export type ActivityEntry = {
  at: number;
  summary: string;
};

// Buffer size, request cap and prompt cap. One number on purpose.
export const MAX_ACTIVITY_ENTRIES = 30;

// Per-summary cap. The middleware truncates to it. The schema rejects longer ones.
export const MAX_ACTIVITY_SUMMARY_CHARS = 580;

/**
 * Moves client timestamps onto the server clock, using the client's posted "now" to get
 * the skew. It runs on arrival because `at` is stored with the turn. An entry cannot be
 * later than its request, so a bad `at` is capped at arrival time. Without `clientNow`,
 * entries stay as they came.
 */
export const toServerClock = (
  entries: ActivityEntry[],
  clientNow: number | undefined,
  serverNow: number,
): ActivityEntry[] => {
  if (clientNow === undefined) return entries;
  const skew = serverNow - clientNow;
  return entries.map((entry) => ({ ...entry, at: Math.min(entry.at + skew, serverNow) }));
};

// What makes two entries the same. Not the timestamp: the client re-posts each entry every
// turn with a slightly different skew correction.
const identity = (entry: ActivityEntry): string => entry.summary;

/**
 * Merges entries stored on earlier turns with the ones this turn posted. The newest
 * occurrence wins. The stored side carries the log across devices. Returns oldest first
 * and capped, in prompt order.
 */
export const mergeActivity = (
  stored: ActivityEntry[],
  posted: ActivityEntry[],
): ActivityEntry[] => {
  const byIdentity = new Map<string, ActivityEntry>();
  for (const entry of [...stored, ...posted]) {
    const key = identity(entry);
    const seen = byIdentity.get(key);
    if (!seen || entry.at > seen.at) byIdentity.set(key, entry);
  }
  return [...byIdentity.values()].sort((a, b) => a.at - b.at).slice(-MAX_ACTIVITY_ENTRIES);
};

// The posted entries the thread does not hold yet. This turn adds these.
export const newActivity = (stored: ActivityEntry[], posted: ActivityEntry[]): ActivityEntry[] => {
  const known = new Set(stored.map(identity));
  return posted.filter((entry) => !known.has(identity(entry)));
};
